import { object, string, type AnyObject } from 'yup';

import type { ApplicationConfiguration } from '@/domains/application/applicationConfiguration.types';
import type { IObject } from '@/types';

import type {
  CheckpointParameterWithCheckpoint,
  RunEvaluationApplication,
  RunEvaluationDerivedData,
} from './RunEvaluationDialog.types';

export const getLatestConfiguration = (
  applicationConfigurations?: ApplicationConfiguration[] | null,
) => {
  if (!applicationConfigurations) return null;

  return applicationConfigurations
    .filter((config) => !config.isDraft)
    .reduce(
      (
        acc: ApplicationConfiguration | null,
        curr: ApplicationConfiguration,
      ) => {
        if (!acc) return curr;
        if (new Date(acc.createdAt) < new Date(curr.createdAt)) {
          return curr;
        }
        return acc;
      },
      null,
    );
};

export const getRunEvaluationDerivedData = (
  latestConfiguration: ApplicationConfiguration | null,
): RunEvaluationDerivedData => {
  if (!latestConfiguration) {
    return {
      latestConfiguration: null,
      runtimeParametersByCheckpoint: [],
      configTimeParametersByCheckpoint: [],
      parameters: [],
    };
  }

  const checkpointsWithParameters = latestConfiguration.checkpoints.filter(
    (checkpoint) => checkpoint.parameters.length > 0,
  );

  const runtimeParametersByCheckpoint = checkpointsWithParameters
    .map((checkpoint) => ({
      ...checkpoint,
      parameters: checkpoint.parameters.filter((parameter) => parameter.isRuntime),
    }))
    .filter((checkpoint) => checkpoint.parameters.length > 0);

  const configTimeParametersByCheckpoint = checkpointsWithParameters
    .map((checkpoint) => ({
      ...checkpoint,
      parameters: checkpoint.parameters.filter((parameter) => !parameter.isRuntime),
    }))
    .filter((checkpoint) => checkpoint.parameters.length > 0);

  const parameters = checkpointsWithParameters
    .map((checkpoint) =>
      checkpoint.parameters.map((parameter) => ({
        ...parameter,
        checkpoint,
      })),
    )
    .flat() as CheckpointParameterWithCheckpoint[];

  return {
    latestConfiguration,
    runtimeParametersByCheckpoint,
    configTimeParametersByCheckpoint,
    parameters,
  };
};

export const getRunEvaluationDefaultValues = (
  application: RunEvaluationApplication | null,
) => {
  return {
    environment: application?.environment || 'Production',
  };
};

export const getRunEvaluationInitialValues = (
  parameters: CheckpointParameterWithCheckpoint[],
  application: RunEvaluationApplication | null,
) => {
  const parameterFields: IObject = {};

  parameters.forEach((parameter) => {
    parameterFields[parameter.paramName] = parameter.paramValue;
  });

  return {
    ...getRunEvaluationDefaultValues(application),
    ...parameterFields,
  };
};

export const getRunEvaluationValidationSchema = (
  parameters: CheckpointParameterWithCheckpoint[],
) => {
  const parametersSchema = parameters.reduce((acc, current) => {
    acc[current.paramName] =
      current.isOptional || !current.checkpoint.isScored
        ? string().nullable()
        : string().required();

    return acc;
  }, {} as AnyObject);

  return object({
    environment: string().required('Environment is required'),
    ...parametersSchema,
  });
};
